import { CircleDollarSign, Phone, Printer, RefreshCcw, StickyNote, Truck, Undo2, User, Voicemail } from "lucide-react";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useNavigate } from "react-router";
import { apiSend } from "../../api";
import { date, dateTime, money } from "../../format";
import { totals } from "../../pricing";
import { Card, Later } from "./Card";
import { CardDialog, cardLabel, RefundDialog, useCardReturn } from "./CardDialogs";
import { Modal } from "./Modal";
import { useLabels } from "./useLabels";
import { useTicketContext } from "./useTicket";

const METHODS = [["check", "Check"], ["check_by_mail", "Check by Mail"], ["paypal", "PayPal"], ["venmo", "Venmo"], ["other", "Other"]] as const;

export function PaymentCard() {
  const { detail, lookups, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [open, setOpen] = useState(false);
  const paid = detail.payments.filter((p) => p.succeeded).reduce((s, p) => s + (p.kind === "refund" ? -1 : 1) * Number(p.amount), 0);
  const taxed = !!detail.customer?.chargeSalesTax && (t.state ?? "").trim().toUpperCase() === "WI";
  const total = lookups ? totals(detail.items, Number(t.insuranceAmount ?? 0), taxed, lookups.pricing).total : 0;
  const balance = Math.max(0, Math.round((total - paid) * 100) / 100);
  const [f, setF] = useState({ method: "check", amount: "", note: "" });
  const [dialog, setDialog] = useState<"card" | "refund" | null>(null);
  useCardReturn();
  const ok = detail.payments.filter((p) => p.succeeded);
  return (
    <Card title="Payment" icon={CircleDollarSign}>
      <div className="pay-head"><span className="strong">Transaction Details</span>
        <span className="hint">{ok.length ? `${ok.length} transaction${ok.length === 1 ? "" : "s"} found` : "No transactions found"} · balance {money(balance)}</span></div>
      {detail.payments.length > 0 && (
        <table className="t-mini">
          <tbody>
            {detail.payments.map((p) => (
              <tr key={p.id} className={p.succeeded ? "" : "failed"} title={p.processorMessage ?? ""}>
                <td>{date(p.paidAt ?? p.createdAt)}</td>
                <td>{p.method.replace(/_/g, " ")}{p.kind === "refund" ? " refund" : ""}{p.succeeded ? "" : " · failed"}
                  {p.processorMessage && p.method !== "card" && <div className="hint">{p.processorMessage}</div>}</td>
                <td className="num">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="btn-row wrap">
        <button type="button" className="btn outline" disabled={saving} onClick={() => { setF({ method: "check", amount: balance ? balance.toFixed(2) : "", note: "" }); setOpen(true); }}>
          <CircleDollarSign size={16} />Pay with Check</button>
        <button type="button" className="btn outline" disabled={saving || !detail.payments.some((p) => Number(p.refundable ?? 0) > 0)}
          title="Refund a card payment, full or partial" onClick={() => setDialog("refund")}><RefreshCcw size={16} />Refund</button>
        <button type="button" className="btn outline" disabled={saving} onClick={() => setDialog("card")}
          title={detail.card ? cardLabel(detail.card) : "No card on file"}><User size={16} />View Payment Profile</button>
      </div>
      {detail.card && <p className="hint">Card on file: {cardLabel(detail.card)}{detail.card.environment === "production" ? " (old system)" : ""}</p>}
      {dialog === "card" && <CardDialog onClose={() => setDialog(null)} />}
      {dialog === "refund" && <RefundDialog onClose={() => setDialog(null)} />}
      {open && (
        <Modal title="Record a payment" onClose={() => setOpen(false)}>
          <form className="mini-form" onSubmit={async (e) => { e.preventDefault(); if (await save("POST", "/payments", f)) setOpen(false); }}>
            <fieldset className="radios"><legend className="lbl">How did the customer pay?</legend>
              {METHODS.map(([v, l]) => (
                <label key={v}><input type="radio" name="method" checked={f.method === v} onChange={() => setF({ ...f, method: v })} /> {l}</label>
              ))}
            </fieldset>
            <label>Amount ($)<input type="number" min="0.01" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} autoFocus /></label>
            <label>Check number or note<input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
            <p className="hint">Balance due {money(balance)}. A payment that covers it moves the ticket to Ready to Ship.</p>
            <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>Record payment</button>
              <button type="button" className="btn outline" onClick={() => setOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </Card>
  );
}

const TRACKING: Record<string, string> = { PRE_TRANSIT: "not scanned yet", TRANSIT: "in transit", DELIVERED: "delivered", RETURNED: "returned to sender", FAILURE: "delivery problem", UNKNOWN: "no tracking yet" };

/** Labels 2 (to the customer) and 3 (spare return label in the box): buy, print, reprint. */
export function ShippingLabelCard() {
  const { detail, saving } = useTicketContext();
  const { busy, print } = useLabels();
  const shown = detail.labels.filter((l) => l.purpose === "outbound" || l.purpose === "spare" || (!l.purpose && !l.toShop));
  const has = shown.some((l) => !l.voidedAt && !l.toShop);
  return (
    <Card title="Shipping Label" icon={Truck}>
      {shown.length > 0 && (
        <ul className="label-list">
          {shown.map((l) => (
            <li key={l.id} className={l.voidedAt ? "voided" : ""}>
              <strong>{l.toShop ? "Spare return label" : "To customer"}</strong> · {l.carrier} {l.serviceLevel}{l.signature ? " (signature)" : ""}
              {l.cost && <> · {money(l.cost)}</>}
              <div className="hint">
                {l.trackingNumber ?? "no tracking number"}
                {l.voidedAt ? ` · voided ${date(l.voidedAt)}${l.refundStatus ? `, refund ${l.refundStatus.toLowerCase()}` : ""}`
                  : l.trackingStatus ? ` · ${TRACKING[l.trackingStatus] ?? l.trackingStatus.toLowerCase()}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="btn-col narrow">
        <button type="button" className="btn outline" disabled={!!busy || saving} onClick={print}
          title={has ? "Reprint label 2 and the spare return label" : "Buy label 2 and the spare return label through Shippo, then print them"}>
          <Printer size={16} aria-hidden="true" /> {busy ?? "Print Shipping Labels"}</button>
      </div>
    </Card>
  );
}

export function ReturnsCard() {
  const { detail, config, reload } = useTicketContext();
  const auth = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!window.confirm(`Create a return ticket for #${detail.ticket.ticketNo}? It starts at $0 with no services, status Returned, at WRP.`)) return;
    setBusy(true);
    try {
      const r = await apiSend<{ ticketNo: number }>(config, auth.user!.id_token ?? "", "POST", `/tickets/${detail.ticket.ticketNo}/return-ticket`);
      navigate(`/tickets/${r.ticketNo}`);
    } catch (e) {
      window.alert((e as Error).message);
      reload();
    } finally { setBusy(false); }
  };
  return (
    <Card title="Returns" icon={Undo2}>
      {detail.ticket.returnOfTicketNo && <p className="hint center">This is a return of <Link to={`/tickets/${detail.ticket.returnOfTicketNo}`}>#{detail.ticket.returnOfTicketNo}</Link></p>}
      {detail.returnTickets.length > 0 && <p className="hint center">Return tickets: {detail.returnTickets.map((n) => <Link key={n} to={`/tickets/${n}`}>#{n} </Link>)}</p>}
      <div className="btn-col narrow"><button type="button" className="btn outline" disabled={busy} onClick={() => void create()}>Create Return Ticket</button></div>
    </Card>
  );
}

const KINDS = [["note", "Note", StickyNote], ["phone_call", "Phone call", Phone], ["voicemail", "Voicemail", Voicemail]] as const;

/** Private notes (staff only), signed and dated: notes, phone calls and voicemails. Status and other
 * system entries are in Messages, marked Staff only (owner, 2026-11-03). */
export function NotesCard() {
  const { detail, save, saving } = useTicketContext();
  const [kind, setKind] = useState<"note" | "phone_call" | "voicemail">("note");
  const [body, setBody] = useState("");
  const entries = detail.notes.map((n) => (
    <li key={n.id} className={`note ${n.kind}`}>
      <div className="meta"><strong>{KINDS.find((k) => k[0] === n.kind)?.[1] ?? "Note"}</strong> · {n.by ?? "Imported"} · {dateTime(n.createdAt)}</div>
      <div className="body">{n.body}</div>
    </li>));
  return (
    <Card title="Private Notes" icon={StickyNote} actions={<span className="hint">Staff only</span>}>
      <form onSubmit={async (e) => { e.preventDefault(); if (await save("POST", "/notes", { kind, body })) setBody(""); }}>
        <div className="kind-tabs" role="radiogroup" aria-label="Kind of note">
          {KINDS.map(([v, l, Icon]) => (
            <label key={v} className={kind === v ? "on" : ""}><input type="radio" name="note-kind" checked={kind === v} onChange={() => setKind(v)} /><Icon size={14} />{l}</label>
          ))}
        </div>
        <textarea className="note-pad" rows={3} value={body} onChange={(e) => setBody(e.target.value)} aria-label="New note"
          placeholder={kind === "note" ? "Add a note…" : kind === "phone_call" ? "What was discussed?" : "What did you leave in the voicemail?"} />
        <div className="btn-row"><button type="submit" className="btn primary" disabled={!body.trim() || saving}>Add</button></div>
      </form>
      <ul className="notes-list">{entries}</ul>
    </Card>
  );
}
